import React from 'react';
import styled from 'styled-components';
import useLocale from '../../hooks/useLocale';
import { usePanchangaNow } from './hooks/usePanchangaNow';
import { classifyMuhurta } from './utils/muhurtaClassifier';
import { isQ3Enabled } from './utils/featureFlags';

const VARIANT_COLORS = {
  green: { bg: 'linear-gradient(135deg, #DFF5E1, #B8E6C1)', border: '#2E7D32', fg: '#1B5E20', dot: '#2E7D32' },
  yellow: { bg: 'linear-gradient(135deg, #FFF6D6, #FCE7A2)', border: '#B8860B', fg: '#6B4E00', dot: '#B8860B' },
  red: { bg: 'linear-gradient(135deg, #FDE1DD, #F7BEB2)', border: '#B71C1C', fg: '#7F0000', dot: '#B71C1C' },
};

const Pill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-radius: 999px;
  background: ${p => VARIANT_COLORS[p.$variant].bg};
  border: 1px solid ${p => VARIANT_COLORS[p.$variant].border};
  color: ${p => VARIANT_COLORS[p.$variant].fg};
  font-size: 14px;
  font-weight: 600;
  font-family: 'Noto Sans Tamil', ${p => p.theme.typography.fontFamily};
  max-width: 320px;
  line-height: 1.3;
`;

const Dot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${p => VARIANT_COLORS[p.$variant].dot};
  flex-shrink: 0;
`;

function labelFor(variant, t) {
  if (variant === 'green') return t.muhurtaGreen || 'Auspicious moment for divination';
  if (variant === 'red') return t.muhurtaRed || 'Consider waiting';
  return t.muhurtaYellow || 'Neutral moment';
}

function ariaFor(variant, t) {
  if (variant === 'green') return t.badgeAriaGreen;
  if (variant === 'red') return t.badgeAriaRed;
  return t.badgeAriaYellow;
}

const MuhurtaQualityBadge = ({ lang = 'en' }) => {
  const locale = useLocale(lang, 'prashnavali');
  const t = (locale && locale.panchangam) || {};
  const { data, loading } = usePanchangaNow();

  if (!isQ3Enabled()) return null;

  const variant = loading ? 'yellow' : classifyMuhurta(data);

  return (
    <Pill $variant={variant} role="status" aria-label={ariaFor(variant, t) || labelFor(variant, t)}>
      <Dot $variant={variant} aria-hidden="true" />
      <span>{loading ? (t.muhurtaLoading || 'Reading the moment…') : labelFor(variant, t)}</span>
    </Pill>
  );
};

export default MuhurtaQualityBadge;
